import { useTranslation } from 'react-i18next';
import { haptic } from '../../../../lib/haptic';
import { PhotoCapture } from '../../../../components/form/PhotoCapture';
import { useSignup } from '../SignupContext';

const fieldCardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-card, 18px)',
  padding: '14px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  letterSpacing: '0.01em',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '16px',
  color: 'var(--color-text)',
  padding: 0,
  fontFamily: 'inherit',
  minHeight: '24px',
};

const hintStyle: React.CSSProperties = {
  fontSize: '12px',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

const errorStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#B91C1C',
  margin: 0,
};

export function ProfileStep() {
  const { t } = useTranslation('auth');
  const { state, setField, setStep } = useSignup();

  const name = state.name;
  const phone = state.phone;
  const photo = state.photoDataUrl;

  const nameTrimmed = name.trim();
  const nameValid = nameTrimmed.length >= 2;
  const photoSet = photo.trim().length > 0;
  const canContinue = nameValid && photoSet;

  const phoneDisplay = phone.length > 5 ? `${phone.slice(0, 5)} ${phone.slice(5)}` : phone;

  const onContinue = () => {
    if (!canContinue) return;
    haptic.success();
    setStep('dl');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '12px' }}>
      <div style={fieldCardStyle}>
        <label style={labelStyle} htmlFor="signup-name">
          {t('signupFields.name')}
        </label>
        <input
          id="signup-name"
          type="text"
          autoComplete="name"
          value={name}
          placeholder={t('signupFields.namePlaceholder')}
          onChange={(e) => setField('name', e.target.value)}
          style={inputStyle}
          maxLength={80}
        />
        {!nameValid && nameTrimmed.length > 0 && (
          <p style={errorStyle}>{t('signupValidation.nameMin')}</p>
        )}
      </div>

      <div style={fieldCardStyle}>
        <label style={labelStyle} htmlFor="signup-phone">
          {t('signupFields.phone')}
        </label>
        <input
          id="signup-phone"
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          value={phoneDisplay}
          placeholder="98765 43210"
          onChange={(e) => setField('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
          style={{ ...inputStyle, letterSpacing: '0.04em' }}
        />
        <p style={hintStyle}>{t('signupFields.phoneHint')}</p>
      </div>

      <div style={fieldCardStyle}>
        <PhotoCapture
          value={photo || null}
          onChange={(dataUrl) => setField('photoDataUrl', dataUrl ?? '')}
          label={t('signupFields.profilePhoto')}
        />
      </div>

      <button
        type="button"
        onClick={onContinue}
        disabled={!canContinue}
        style={{
          marginTop: '8px',
          width: '100%',
          minHeight: 'var(--touch-target, 52px)',
          borderRadius: 'var(--radius-pill, 9999px)',
          border: 'none',
          background: canContinue ? 'var(--color-btn-primary)' : 'var(--color-border)',
          color: canContinue ? '#fff' : 'var(--color-text-secondary)',
          fontSize: '16px',
          fontWeight: 700,
          cursor: canContinue ? 'pointer' : 'not-allowed',
          transition: 'background-color 150ms ease, color 150ms ease',
          fontFamily: 'inherit',
        }}
      >
        {t('signupActions.continue')}
      </button>
    </div>
  );
}
